import React, { useState, useRef, useEffect } from 'react';
import { storage } from '../../firebase/config';
import './ProfileImageUpload.css';

const ProfileImageUpload = ({ currentImage, onImageUpload }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [previewUrl, setPreviewUrl] = useState('');
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  // Initialiser l'aperçu avec l'image actuelle
  useEffect(() => {
    if (currentImage) {
      setPreviewUrl(currentImage);
    }
  }, [currentImage]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Vérifier le type de fichier
    if (!file.type.match('image.*')) {
      setError('Veuillez sélectionner une image valide (JPG, PNG, GIF)');
      return;
    }

    // Vérifier la taille du fichier (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError('La taille de l\'image ne doit pas dépasser 2MB');
      return;
    }

    setError(null);

    // Créer un aperçu de l'image
    const reader = new FileReader();
    reader.onload = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);

    // Lancer l'upload
    uploadImage(file);
  };

  const uploadImage = async (file) => {
    setUploading(true);
    setProgress(0);

    // Créer un nom de fichier unique basé sur la date et le nom original
    const fileName = `profile_${Date.now()}_${file.name}`;
    const storageRef = storage.ref();
    const imageRef = storageRef.child(`profile_images/${fileName}`);

    try {
      // Commencer l'upload
      const uploadTask = imageRef.put(file);

      // Écouter les changements de statut de l'upload
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          // Mettre à jour la progression
          const progressValue = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setProgress(progressValue);
        },
        (error) => {
          // Gérer les erreurs
          console.error('Erreur pendant l\'upload:', error);
          setError('Une erreur est survenue pendant l\'upload.');
          setUploading(false);
        },
        async () => {
          // Upload complété avec succès
          // Récupérer l'URL de l'image
          const downloadUrl = await imageRef.getDownloadURL();
          
          // Appeler la fonction de callback avec l'URL
          if (onImageUpload) {
            onImageUpload(downloadUrl);
          }
          
          setUploading(false);
        }
      );
    } catch (error) {
      console.error('Erreur lors de l\'initialisation de l\'upload:', error);
      setError('Une erreur est survenue. Veuillez réessayer.');
      setUploading(false);
    }
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="profile-image-upload">
      <div className="image-preview-container">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Aperçu du profil"
            className="image-preview"
          />
        ) : (
          <div className="image-placeholder">
            <i className="fas fa-user"></i>
          </div>
        )}
        
        {uploading && (
          <div className="upload-progress-overlay">
            <div 
              className="upload-progress-bar"
              style={{ width: `${progress}%` }}
            ></div>
            <div className="upload-progress-text">{Math.round(progress)}%</div>
          </div>
        )}
      </div>
      
      <div className="upload-actions">
        <button
          type="button"
          className="btn btn-outline"
          onClick={triggerFileInput}
          disabled={uploading}
        >
          {previewUrl ? 'Changer la photo' : 'Ajouter une photo'}
        </button>
        
        <input
          type="file"
          ref={fileInputRef}
          className="file-input"
          accept="image/*"
          onChange={handleFileSelect}
          disabled={uploading}
        />
      </div>
      
      {error && <div className="upload-error">{error}</div>}
      
      <div className="upload-info">
        Format recommandé : JPG ou PNG, max 2MB,<br />
        Dimensions idéales : 300x300 pixels
      </div>
    </div>
  );
};

export default ProfileImageUpload;